import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Package, 
  AlertTriangle, 
  TrendingUp, 
  Calendar,
  ArrowUpRight
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DepotDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const [reorderAlerts, setReorderAlerts] = useState([]);
  const [depot, setDepot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, trendRes, alertsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/analytics/sales-trend`, { headers: getAuthHeaders() }),
          axios.get(`${API}/inventory/reorder-alerts`, { headers: getAuthHeaders() })
        ]);
        setStats(statsRes.data);
        setSalesTrend(trendRes.data);
        setReorderAlerts(alertsRes.data);

        if (user?.depot_id) {
          const depotRes = await axios.get(`${API}/depots/${user.depot_id}`, { headers: getAuthHeaders() });
          setDepot(depotRes.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders, user?.depot_id]);

  const kpiCards = [
    {
      title: 'Total Stock',
      value: stats?.total_stock?.toLocaleString() || '0',
      icon: Package,
      color: 'bg-blue-500'
    },
    {
      title: 'Low Stock Items',
      value: stats?.low_stock_alerts || '0',
      icon: AlertTriangle,
      color: 'bg-red-500'
    },
    {
      title: 'Monthly Sales',
      value: stats?.total_sales_this_month?.toLocaleString() || '0',
      icon: TrendingUp,
      color: 'bg-green-500'
    },
    {
      title: 'Total Items',
      value: stats?.total_items || '0',
      icon: Calendar,
      color: 'bg-purple-500'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="depot-dashboard">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {user?.depot_name || 'Depot'} Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Overview of your depot's inventory and sales</p>
      </div>

      {/* Upload Status */}
      {depot && (
        <Card className="animate-fade-in bg-slate-50 border-slate-200" data-testid="upload-status">
          <CardContent className="p-4">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm text-slate-500">Last Stock Upload</p>
                <p className="font-medium">
                  {depot.last_stock_upload 
                    ? new Date(depot.last_stock_upload).toLocaleDateString('en-US', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) 
                    : 'Never'}
                </p>
              </div>
              <div className="h-8 w-px bg-slate-300" />
              <div>
                <p className="text-sm text-slate-500">Last Sales Upload</p>
                <p className="font-medium">
                  {depot.last_sales_upload 
                    ? new Date(depot.last_sales_upload).toLocaleDateString('en-US', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) 
                    : 'Never'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((card, index) => (
          <Card 
            key={card.title} 
            className="card-hover animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
            data-testid={`kpi-${card.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{card.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2 tabular-nums">
                    {card.value}
                  </p>
                </div>
                <div className={`${card.color} p-3 rounded-xl`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend */}
        <Card className="animate-fade-in animation-delay-200" data-testid="depot-sales-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sales Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              {salesTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesTrend}>
                    <defs>
                      <linearGradient id="colorDepotSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="month" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="total_sales" 
                      stroke="#2563eb" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorDepotSales)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-slate-400">No sales data yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reorder Alerts */}
        <Card className="animate-fade-in animation-delay-300" data-testid="depot-reorder-alerts">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reorder Alerts
            </CardTitle>
            {reorderAlerts.length > 0 && (
              <Badge variant="destructive">{reorderAlerts.length} items</Badge>
            )}
          </CardHeader>
          <CardContent>
            {reorderAlerts.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-green-300 mx-auto mb-3" />
                <p className="text-slate-500">All stock levels are healthy!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reorderAlerts.slice(0, 5).map((item) => (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg border-l-4 border-red-500"
                  >
                    <div>
                      <p className="font-medium text-sm text-red-900">{item.item_name}</p>
                      <p className="text-xs text-red-700 font-mono">{item.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-900 tabular-nums">{item.quantity}</p>
                      <p className="text-xs text-red-700">of {item.buffer_level} min</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DepotDashboard;
